// Hero visual: presentational WhatsApp conversation.
// Purely UI. No real chat logic, no capability invented.
// Sequence: vocale con modifica/extra → Mastro segna l'extra sul lavoro giusto → foto con didascalia → foto collegata all'extra.
import { ArrowLeft, MoreVertical, Play, CheckCheck, Mic } from "lucide-react";

const Waveform = ({ played = 6, total = 22 }) => (
  <div className="flex h-5 items-center gap-[3px]">
    {Array.from({ length: total }).map((_, i) => {
      const h = 4 + ((i * 7 + 3) % 14);
      return (
        <span
          key={i}
          className={`w-[2px] rounded-full ${i < played ? "bg-teal-700" : "bg-neutral-300"}`}
          style={{ height: `${h}px` }}
        />
      );
    })}
  </div>
);

const OutgoingBubble = ({ children, meta, tail = false }) => (
  <div className="flex justify-end">
    <div
      className={`relative max-w-[85%] rounded-2xl bg-[#D9FDD3] px-2.5 py-1.5 text-[13.5px] leading-snug text-neutral-900 shadow-[0_1px_0.5px_rgba(0,0,0,0.13)] ${
        tail ? "rounded-tr-md" : ""
      }`}
    >
      {children}
      <div className="mt-0.5 flex items-center justify-end gap-1 text-[10.5px] text-neutral-500">
        <span>{meta}</span>
        <CheckCheck className="h-3.5 w-3.5 text-[#53BDEB]" />
      </div>
    </div>
  </div>
);

const IncomingBubble = ({ children, meta, tail = false }) => (
  <div className="flex justify-start">
    <div
      className={`max-w-[85%] rounded-2xl bg-white px-3 py-2 text-[13.5px] leading-snug text-neutral-900 shadow-[0_1px_0.5px_rgba(0,0,0,0.13)] ${
        tail ? "rounded-tl-md" : ""
      }`}
    >
      {children}
      <div className="mt-1 flex items-center justify-end gap-1 text-[10.5px] text-neutral-400">
        <span>{meta}</span>
      </div>
    </div>
  </div>
);

export const WhatsAppConversation = () => {
  return (
    <div
      data-testid="landing-hero-whatsapp"
      className="mx-auto w-full max-w-[320px] overflow-hidden rounded-[24px] border border-neutral-200 bg-white shadow-[0_16px_40px_-20px_rgba(15,23,42,0.22)]"
    >
      {/* Top bar — real WhatsApp Android look */}
      <div className="flex items-center gap-3 border-b border-neutral-200 bg-white px-3 py-2 text-neutral-900">
        <ArrowLeft className="h-5 w-5 shrink-0 text-neutral-500" aria-hidden="true" />
        <div className="flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded-full bg-white ring-1 ring-neutral-200">
          <img
            src="/mastro-mark.png"
            alt="Mastro"
            draggable={false}
            className="h-full w-full object-contain p-1.5"
          />
        </div>
        <div className="min-w-0 flex-1 leading-tight">
          <div className="truncate text-[15px] font-semibold">Il Tuo Mastro</div>
          <div className="truncate text-[11.5px] text-neutral-500">online</div>
        </div>
        <MoreVertical className="h-5 w-5 shrink-0 text-neutral-500" aria-hidden="true" />
      </div>

      {/* Chat area */}
      <div
        className="space-y-1 px-3 pb-2.5 pt-2 sm:px-3.5"
        style={{
          backgroundColor: "#EFEAE2",
          backgroundImage: "radial-gradient(rgba(0,0,0,0.035) 1px, transparent 1px)",
          backgroundSize: "14px 14px",
        }}
      >
        {/* Date pill */}
        <div className="flex justify-center pb-0.5">
          <span className="rounded-md bg-white/85 px-2 py-0.5 text-[10.5px] font-medium text-neutral-600 shadow-[0_1px_0.5px_rgba(0,0,0,0.1)]">
            oggi
          </span>
        </div>

        {/* Outgoing — voice note (concept: "aggiungi 2 punti luce in cucina, li ha chiesti il geometra") */}
        <OutgoingBubble meta="09:42" tail>
          <div className="flex items-center gap-2 py-0.5 pr-1">
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-neutral-200 text-neutral-500">
              <Mic className="h-3.5 w-3.5" />
            </div>
            <button
              type="button"
              aria-hidden="true"
              tabIndex={-1}
              className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-neutral-600"
            >
              <Play className="h-4 w-4 fill-current" />
            </button>
            <Waveform played={6} total={22} />
            <span className="pl-1 text-[11px] text-neutral-500">0:11</span>
          </div>
        </OutgoingBubble>

        {/* Incoming — Mastro aggiunge l'extra al lavoro giusto */}
        <div className="pt-0.5">
          <IncomingBubble meta="09:42" tail>
            <span>Aggiunto al lavoro</span>
            <div className="mt-1.5 space-y-0.5 rounded-lg border-l-[3px] border-[#027EB5] bg-[#F0F7FB] px-2.5 py-1.5">
              <div className="text-[13px] font-semibold text-neutral-900">
                Mario Rossi — Via Roma 12
              </div>
              <div className="mt-1 text-[11px] font-semibold uppercase tracking-wide text-[#027EB5]">
                Extra
              </div>
              <div className="text-[13px] text-neutral-700">
                2 punti luce in cucina
              </div>
              <div className="text-[12px] text-neutral-500">
                Richiesto dal geometra
              </div>
            </div>
          </IncomingBubble>
        </div>

        {/* Outgoing — foto della parete con didascalia */}
        <div className="pt-0.5">
          <OutgoingBubble meta="11:20" tail>
            <div className="-mx-1 -mt-0.5 mb-1 overflow-hidden rounded-xl">
              <img
                src="/landing/predisposizione-elettrica.jpg"
                alt=""
                aria-hidden="true"
                draggable={false}
                className="block h-32 w-full object-cover"
              />
            </div>
            <span className="px-0.5 text-[13.5px]">Sono su questa parete.</span>
          </OutgoingBubble>
        </div>

        {/* Incoming — foto collegata all'extra */}
        <IncomingBubble meta="11:20" tail>
          <span>Foto aggiunta all&rsquo;extra</span>
          <div className="mt-1 text-[13px] font-semibold text-[#027EB5]">
            2 punti luce in cucina
          </div>
        </IncomingBubble>
      </div>
    </div>
  );
};
